import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faEdit, faArrowUp, faArrowDown, faSync, faUsers, 
    faSearch, faFileExcel, faFilePdf, faUserShield, faUtensils, 
    faCashRegister, faConciergeBell, faUser, faTimes, faUserEdit
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import baseURL from '../../url';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Register from '../Register/Register';
import './UsuariosData.css';

export default function UsuariosData() {
    const [usuarios, setUsuarios] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [filtroRol, setFiltroRol] = useState('');
    const [ordenInvertido, setOrdenInvertido] = useState(false);
    
    // Modal Editar Usuario y Rol
    const [modalEditarVisible, setModalEditarVisible] = useState(false);
    const [usuarioEditar, setUsuarioEditar] = useState(null);
    const [nuevoRol, setNuevoRol] = useState('usuario');
    const [cargandoRol, setCargandoRol] = useState(false);

    useEffect(() => {
        cargarUsuarios();
    }, []);

    const cargarUsuarios = () => {
        fetch(`${baseURL}/usuariosGet.php`)
            .then(res => res.json())
            .then(data => setUsuarios(data.usuarios || []))
            .catch(err => console.error('Error al cargar usuarios:', err));
    };

    const eliminarUsuario = (idUsuario) => {
        Swal.fire({
            title: '¿Eliminar usuario?',
            text: 'Esta acción dará de baja el acceso al sistema para este usuario.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(`${baseURL}/usuariosGet.php?idUsuario=${idUsuario}`, {
                    method: 'DELETE',
                })
                    .then(res => res.json())
                    .then(data => {
                        Swal.fire('¡Eliminado!', data.mensaje, 'success');
                        cargarUsuarios();
                    })
                    .catch(err => {
                        console.error('Error al eliminar usuario:', err);
                        toast.error('Error al eliminar usuario');
                    });
            }
        });
    };

    const abrirModalEditar = (item) => {
        setUsuarioEditar(item);
        setNuevoRol(item.rol || 'usuario');
        setModalEditarVisible(true);
    };

    const cerrarModalEditar = () => {
        setModalEditarVisible(false);
        setUsuarioEditar(null);
    };

    const guardarEdicionUsuario = async (e) => {
        if (e) e.preventDefault();
        if (!usuarioEditar) return;

        setCargandoRol(true);
        try {
            const res = await fetch(`${baseURL}/usuariosGet.php?idUsuario=${usuarioEditar.idUsuario}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rol: nuevoRol }),
            });
            const data = await res.json();
            if (data.error) {
                toast.error(data.error);
            } else {
                toast.success(data.mensaje || 'Rol actualizado con éxito');
                cargarUsuarios();
                cerrarModalEditar();
            }
        } catch (err) {
            console.error('Error al editar rol:', err);
            toast.error('Error al actualizar el rol');
        } finally {
            setCargandoRol(false);
        }
    };

    const invertirOrden = () => {
        setUsuarios([...usuarios].reverse());
        setOrdenInvertido(!ordenInvertido);
    };

    const usuariosFiltrados = usuarios.filter(u => {
        const query = busqueda.toLowerCase();
        const coincideBusqueda = !busqueda ||
            (u.nombre || '').toLowerCase().includes(query) ||
            (u.email || '').toLowerCase().includes(query) ||
            u.idUsuario.toString().includes(query);

        const coincideRol = !filtroRol || (u.rol || '').toLowerCase() === filtroRol.toLowerCase();
        return coincideBusqueda && coincideRol;
    });

    const descargarExcel = () => {
        const data = usuariosFiltrados.map(u => ({
            'ID': u.idUsuario,
            'Nombre': u.nombre,
            'Email': u.email,
            'Rol': u.rol,
            'Fecha Registro': u.createdAt,
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
        XLSX.writeFile(wb, 'usuarios_sistema.xlsx');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF();
        pdf.text('Lista de Usuarios y Roles', 14, 12);

        const columns = [
            { title: 'ID', dataKey: 'id' },
            { title: 'Nombre', dataKey: 'nombre' },
            { title: 'Email', dataKey: 'email' },
            { title: 'Rol', dataKey: 'rol' },
            { title: 'Fecha', dataKey: 'createdAt' },
        ];

        const data = usuariosFiltrados.map(u => ({
            id: u.idUsuario,
            nombre: u.nombre,
            email: u.email,
            rol: u.rol,
            createdAt: u.createdAt,
        }));

        pdf.autoTable({
            startY: 18,
            columns: columns,
            body: data,
        });

        pdf.save('usuarios_sistema.pdf');
    };

    // Métricas por roles
    const totalUsuarios = usuarios.length;
    const countAdmins = usuarios.filter(u => u.rol === 'admin').length;
    const countMeseros = usuarios.filter(u => u.rol === 'mesero').length;
    const countCocineros = usuarios.filter(u => u.rol === 'cocinero').length;
    const countCajeros = usuarios.filter(u => u.rol === 'cajero').length;

    const renderBadgeRol = (rol) => {
        const r = (rol || '').toLowerCase();
        let icon = faUser;
        let badgeClass = 'badgeRolUser';
        let label = 'Usuario';

        if (r === 'admin') {
            icon = faUserShield;
            badgeClass = 'badgeRolAdmin';
            label = 'Administrador';
        } else if (r === 'mesero') {
            icon = faConciergeBell;
            badgeClass = 'badgeRolMesero';
            label = 'Mesero';
        } else if (r === 'cocinero') {
            icon = faUtensils;
            badgeClass = 'badgeRolCocina';
            label = 'Cocinero';
        } else if (r === 'cajero') {
            icon = faCashRegister;
            badgeClass = 'badgeRolCajero';
            label = 'Cajero';
        }

        return (
            <span className={`adminBadge ${badgeClass}`}>
                <FontAwesomeIcon icon={icon} /> {label}
            </span>
        );
    };

    return (
        <div className="adminPageContainer">
            <ToastContainer />

            {/* Cabecera Principal */}
            <div className="adminHeaderSection">
                <div className="adminHeaderTitle">
                    <h2><FontAwesomeIcon icon={faUsers} /> Gestión de Usuarios y Roles</h2>
                    <p>Administra las cuentas del personal del restaurante y asigna sus permisos y roles correspondientes.</p>
                </div>
                <div className="adminHeaderActions">
                    <Register onUserCreated={cargarUsuarios} />
                    <button className="adminExportBtn btnExcel" onClick={descargarExcel} title="Exportar a Excel">
                        <FontAwesomeIcon icon={faFileExcel} /> Excel
                    </button>
                    <button className="adminExportBtn btnPdf" onClick={descargarPDF} title="Exportar a PDF">
                        <FontAwesomeIcon icon={faFilePdf} /> PDF
                    </button>
                    <button className="adminActionBtn" onClick={cargarUsuarios} title="Recargar usuarios">
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>
            </div>

            {/* Métricas de Roles */}
            <div className="adminKpisMiniGrid">
                <div className="adminKpiMiniCard">
                    <span>Total Personal</span>
                    <strong>{totalUsuarios}</strong>
                </div>
                <div className="adminKpiMiniCard kpiAdmin">
                    <span>Admins</span>
                    <strong>{countAdmins}</strong>
                </div>
                <div className="adminKpiMiniCard kpiMesero">
                    <span>Meseros</span>
                    <strong>{countMeseros}</strong>
                </div>
                <div className="adminKpiMiniCard kpiCocina">
                    <span>Cocineros</span>
                    <strong>{countCocineros}</strong>
                </div>
                <div className="adminKpiMiniCard kpiCajero">
                    <span>Cajeros</span>
                    <strong>{countCajeros}</strong>
                </div>
            </div>

            {/* Toolbar */}
            <div className="adminToolbar">
                <div className="adminSearchBox">
                    <FontAwesomeIcon icon={faSearch} />
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre, correo electrónico o ID..." 
                        value={busqueda} 
                        onChange={(e) => setBusqueda(e.target.value)} 
                    />
                </div>

                <div className="adminFiltersRow">
                    <select value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)}>
                        <option value="">Todos los Roles</option>
                        <option value="admin">Administrador</option>
                        <option value="mesero">Mesero</option>
                        <option value="cocinero">Cocinero</option>
                        <option value="cajero">Cajero</option>
                        <option value="usuario">Usuario / Cliente</option>
                    </select>

                    <button className="adminActionBtn" onClick={invertirOrden} title="Invertir orden">
                        <FontAwesomeIcon icon={ordenInvertido ? faArrowUp : faArrowDown} />
                    </button>
                </div>
            </div>

            {/* Tabla de Usuarios */}
            <div className="adminTableCard">
                <div className="adminTableResponsive">
                    <table className="adminTable">
                        <thead>
                            <tr>
                                <th style={{ width: '70px' }}># ID</th>
                                <th>Nombre Completo</th>
                                <th>Correo Electrónico</th>
                                <th>Rol Asignado</th>
                                <th>Fecha de Registro</th>
                                <th style={{ textAlign: 'center', width: '120px' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuariosFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="adminEmptyRow">
                                        No se encontraron usuarios con los filtros aplicados.
                                    </td>
                                </tr>
                            ) : (
                                usuariosFiltrados.map(item => (
                                    <tr key={item.idUsuario}>
                                        <td><strong>#{item.idUsuario}</strong></td>
                                        <td>
                                            <div className="userNameCell">
                                                <div className="userMiniAvatar">
                                                    {(item.nombre || 'U').charAt(0).toUpperCase()}
                                                </div>
                                                <strong>{item.nombre}</strong>
                                            </div>
                                        </td>
                                        <td><span className="userEmailText">{item.email}</span></td>
                                        <td>{renderBadgeRol(item.rol)}</td>
                                        <td><span className="fechaSpan">{item.createdAt}</span></td>
                                        <td>
                                            <div className="adminActionBtnGroup" style={{ justifyContent: 'center' }}>
                                                <button 
                                                    className="btnTableAction btnEditar" 
                                                    onClick={() => abrirModalEditar(item)}
                                                    title="Editar usuario y asignar rol"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button 
                                                    className="btnTableAction btnBorrar" 
                                                    onClick={() => eliminarUsuario(item.idUsuario)}
                                                    title="Eliminar usuario"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Editar Usuario / Rol */}
            {modalEditarVisible && usuarioEditar && (
                <div className="adminModalOverlay" onClick={cerrarModalEditar}>
                    <div className="adminModalContent modalSmallSimple" onClick={(e) => e.stopPropagation()}>
                        <div className="adminModalHeader">
                            <h3><FontAwesomeIcon icon={faUserEdit} /> Modificar Usuario #{usuarioEditar.idUsuario}</h3>
                            <button className="adminModalClose" onClick={cerrarModalEditar}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={guardarEdicionUsuario}>
                            <div className="adminModalBody">
                                <div className="userEditSummaryCard">
                                    <div className="userMiniAvatar large">
                                        {(usuarioEditar.nombre || 'U').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="userEditMeta">
                                        <strong>{usuarioEditar.nombre}</strong>
                                        <span>{usuarioEditar.email}</span>
                                    </div>
                                </div>

                                <div className="adminFormField">
                                    <label>Rol y Nivel de Acceso en el Restaurante:</label>
                                    <select
                                        value={nuevoRol}
                                        onChange={(e) => setNuevoRol(e.target.value)}
                                        required
                                        autoFocus
                                    >
                                        <option value="admin">Administrador (Acceso total al sistema y configuraciones)</option>
                                        <option value="mesero">Mesero (Gestión de mesas y pedidos)</option>
                                        <option value="cocinero">Cocinero (Panel de cocina KDS)</option>
                                        <option value="cajero">Cajero (Punto de Venta POS y cobros)</option>
                                        <option value="usuario">Usuario / Cliente (Solo visualización y compras)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="adminModalFooter">
                                <button type="button" className="adminBtnSecondary" onClick={cerrarModalEditar} disabled={cargandoRol}>
                                    Cancelar
                                </button>
                                <button type="submit" className="adminBtnPrimary" disabled={cargandoRol}>
                                    {cargandoRol ? 'Guardando...' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
